import React, { useCallback } from "react";
import styles from "./styles.module.scss";
import useDatafileIcons from "app/components/inputs/SelectBox/helpers/DatafileIcons";
import { MediaTypes } from "./index.types";
import playIcon from "app/images/play_btn.svg";
import duration from "app/components/inputs/SelectBox/helpers/DurationConverter";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { cls } from "app/utils";
import { selectedMedia } from "app/actions/media-library-data";
import checkmark from "../../../../public/images/checkmark.svg";
import { Spinner } from "app/components/Loading";

interface Props {
  showTags?: boolean;
  showPlayIcon?: boolean;
  thumbnailClassname?: string;
  hasGradientOverlay?: boolean;
  data: MediaTypes;
}

const ResourceCard = ({
  data,
  showTags = true,
  showPlayIcon = true,
  thumbnailClassname,
  hasGradientOverlay = true,
}: Props) => {
  const dispatch = useDispatch();

  const selectedMediaData = useSelector(
    (state: RootState) => state.mediaLibraryData.selectedMedia,
  );

  const mediaSelectHandler = (data: MediaTypes) => {
    if (selectedMediaData && selectedMediaData.id === data.id) {
      dispatch(selectedMedia(null));
    } else {
      dispatch(selectedMedia(data));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "short",
      year: "numeric",
    };

    const formattedDate = date.toLocaleDateString("en-US", options);

    return formattedDate;
  };
  const { dataFileIconHandler } = useDatafileIcons();

  const displayThumbnail = useCallback((data: MediaTypes) => {
    if (!data || !data.object) {
      return null;
    }

    if (data.object === "video") {
      return data.processed ? (
        data.thumbnails && data.thumbnails.default && (
          <>
            {data && data.duration > 0 && showPlayIcon && (
              <div className={styles.play_wrapper}>
                <img src={playIcon} alt="play" />
                <span>{duration(data.duration)}</span>
              </div>
            )}
            <img src={data.thumbnails.default} alt="media thumbnail" />
          </>
        )
      ) : (
        <span className={styles.loading_container}>
          <Spinner />
        </span>
      );
    } else if (data.object === "image") {
      return data.mediumUrl ? (
        <img src={data.mediumUrl} alt="media thumbnail" />
      ) : (
        data.originalUrl && <img src={data.originalUrl} alt="media thumbnail" />
      );
    } else if (data.object === "datafile") {
      return <img src={dataFileIconHandler(data.extension)} alt="datafile" />;
    } else if (data.object === "audio") {
      return <img src={dataFileIconHandler(data.object)} alt="datafile" />;
    }

    return null;
  }, []);

  return (
    <div key={data.id}>
      <div onClick={() => mediaSelectHandler(data)}>
        <div
          className={cls(styles.thumbnail_wrapper, thumbnailClassname, {
            [styles.selectedMediaData]:
              selectedMediaData && selectedMediaData.id === data.id,
          })}
        >
          {selectedMediaData && selectedMediaData.id === data.id && (
            <span className={styles.checkmark_container}>
              <img
                src={checkmark}
                alt="checkmark"
                className={styles.checkmark}
              />
            </span>
          )}
          {displayThumbnail(data)}
          <div
            className={cls({ [styles.thumbnail_overlay]: hasGradientOverlay })}
          />
          {showTags && (
            <div className={styles.tags_overlay}>
              {data.tags && data.tags.length ? (
                <>
                  #
                  {data.tags.map((tag) => {
                    return (
                      <span key={tag} className={styles.tag}>
                        {tag}
                        {data.tags.length > 1 ? "," : ""}
                      </span>
                    );
                  })}
                </>
              ) : (
                "No Tags"
              )}
            </div>
          )}
        </div>
      </div>
      <div className={styles.media_info}>
        <p className={styles.post_count}>
          {data.postsCount} Post{data.postsCount > 1 && "s"}
        </p>
        <p className={styles.posted_at}>{formatDate(data.createdAt)}</p>
      </div>
    </div>
  );
};

export default ResourceCard;
